import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { CreateLoadResponse } from 'lib/api/createLoad';
import { CreateLoadMGBRequest } from 'pages/QuoteView/LoadBuilding/MercuryGateBoomiSectionForms/buildRequestBody';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

/**
 * Posts a MercuryGate-Boomi create-load request to `POST /load`.
 *
 * Wraps the response in `neverthrow` to mirror the existing `createLoad`
 * helper while preserving the exact MG-Boomi payload (no pallet-count
 * coercion or other Mcleod/Aljex-flavoured massaging).
 */
export async function createLoadMercuryGateBoomi(
  data: CreateLoadMGBRequest
): Promise<Result<CreateLoadResponse, ApiError>> {
  try {
    const response = await axios.post<CreateLoadResponse>('/load', data);
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'createLoadMercuryGateBoomi' });

    if (!isAxiosError(error)) {
      return err({ message: 'Unable to create load.' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    if (error.message === 'Extension context invalidated.') {
      throw error;
    }

    const data = error.response?.data as {
      message?: string;
      code?: string;
      load?: CreateLoadResponse['load'];
      level?: 'error' | 'warning';
      errorLevel?: 'error' | 'warning';
    };

    return err({
      message: data?.message || 'Unable to create load.',
      status: error.response?.status,
      response: data,
      load: data?.load,
      level: data?.level || data?.errorLevel,
    });
  }
}
